import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { translations } from './translations/translations';
import './App.css';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Products from './pages/Products';
import Contact from './pages/Contact';
import FPC from './pages/FPC';
import Export from './pages/Export';
import Mushroom from './pages/Mushroom';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// ProtectedRoute — redirects to login if no token
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

// 404 Not Found Component
const NotFound = () => {
  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow flex items-center justify-center bg-gray-50">
        <div className="text-center px-4 py-16">
          <div className="text-9xl mb-4">🌾</div>
          <h1 className="text-6xl font-bold text-green-800 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Page Not Found</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Sorry, the page you are looking for doesn't exist or has been moved.
          </p>
          <Link 
            to="/" 
            className="inline-block bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors"
          >
            Go Back Home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  const [language, setLanguage] = useState('en');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'mr' : 'en');
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      <Router>
        <Routes>
          {/* Admin routes without Navbar/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Public routes with Navbar/Footer */}
          <Route path="/" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Home />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/about" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <About />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/services" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Services />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/products" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Products />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/contact" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Contact />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/fpc" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <FPC />
              </main>
              <Footer />
            </div>
          } />
          
          <Route path="/export" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Export />
              </main>
              <Footer />
            </div>
          } />

          <Route path="/mushroom" element={
            <div className="App min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Mushroom />
              </main>
              <Footer />
            </div>
          } />
          
          {/* 404 Catch-all route - must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </LanguageContext.Provider>
  );
}

export default App;
